/**
 * Загрузка Telegram WebApp SDK.
 *
 * СКРИПТ ЛЕЖИТ У НАС (`./vendor/telegram-web-app.js`), а не на telegram.org.
 * Домен telegram.org заблокирован в России, и внутри мини-аппа тоже: вебвью
 * тянет скрипт из сети тем же путём, что и браузер, — из самого клиента он не
 * «приезжает». Пока SDK грузился с telegram.org, в России он не грузился
 * никогда: тег отваливался по таймауту, `window.Telegram` не появлялся, и
 * мини-апп оставался без ready()/expand() и без входа.
 *
 * Файл вендорится как есть, копией официального, и подключается тегом
 * `<script>` — то есть исполняется ровно так, как задумано в Telegram
 * (обычный скрипт, не ES-модуль). Vite отдаёт его с нашего домена и с хэшем
 * в имени, так что кэш обновляется вместе со сборкой.
 *
 * Тег по-прежнему не стоит в <head>: 116 КБ нужны только внутри Telegram, а
 * обычному посетителю сайта — нет. Грузим из кода и только при признаках
 * запуска из клиента.
 *
 * ВХОД ОТ ЭТОЙ ЗАГРУЗКИ НЕ ЗАВИСИТ: initData читается прямо из адреса
 * (telegramLaunchParams.ts). SDK нужен для ready()/expand(), нативных кнопок
 * и темы.
 */
import type { TelegramWebApp } from './useTelegramApp'
import { hasLaunchParamsInLocation, readTelegramInitData } from './telegramLaunchParams'

// `?url` — Vite положит файл в сборку как есть и отдаст путь к нему.
import telegramSdkUrl from './vendor/telegram-web-app.js?url'

export const TELEGRAM_SDK_URL = telegramSdkUrl

/** Свой домен отвечает быстро; дольше ждать нечего. */
export const TELEGRAM_SDK_TIMEOUT_MS = 10_000

/** Ключ, под которым SDK сохраняет параметры запуска — переживает перезагрузку. */
const TELEGRAM_INIT_PARAMS_KEY = '__telegram__initParams'

/**
 * Признаки запуска внутри Telegram, видимые БЕЗ загрузки скрипта.
 * Нужны, чтобы обычный посетитель сайта не тянул лишние 116 КБ.
 */
export function looksLikeTelegram(): boolean {
    if (typeof window === 'undefined') return false

    if (window.Telegram?.WebApp) return true
    if ('TelegramWebviewProxy' in window) return true
    // Клиент Telegram дописывает параметры запуска в адрес (обычно в hash).
    if (hasLaunchParamsInLocation()) return true
    // Хэш роутер уже мог снять — но initData этого запуска мы сохранили сами.
    if (readTelegramInitData() !== null) return true

    try {
        if (sessionStorage.getItem(TELEGRAM_INIT_PARAMS_KEY)) return true
    } catch {
        // sessionStorage недоступен — признаком меньше, не беда
    }

    return false
}

let pending: Promise<TelegramWebApp> | null = null

/**
 * Подгружает SDK и отдаёт `window.Telegram.WebApp`.
 *
 * Промис отклоняется, если скрипт не загрузился (ошибка сети или таймаут);
 * тег при этом удаляется. Отклонение больше НЕ значит «мы не в Telegram» —
 * вход к этому моменту уже сделан по initData из адреса, и отказ стоит
 * ровно того, что в нём есть: мини-апп останется без ready()/expand().
 */
export function loadTelegramSdk(timeoutMs: number = TELEGRAM_SDK_TIMEOUT_MS): Promise<TelegramWebApp> {
    if (pending) return pending

    pending = new Promise<TelegramWebApp>((resolve, reject) => {
        const existing = typeof window === 'undefined' ? undefined : window.Telegram?.WebApp
        if (existing) {
            resolve(existing)
            return
        }

        const script = document.createElement('script')
        script.src = TELEGRAM_SDK_URL
        script.async = true
        // Сторож устаревшего кэша в main.ts перезагружает страницу на любой
        // упавший <script>. Этому падать разрешено — помечаем.
        script.dataset.optional = 'true'

        let timer: ReturnType<typeof setTimeout> | null = null

        const stopWaiting = () => {
            if (timer !== null) {
                clearTimeout(timer)
                timer = null
            }
            script.onload = null
            script.onerror = null
        }

        const giveUp = (reason: string) => {
            stopWaiting()
            script.remove()
            reject(new Error(reason))
        }

        script.onload = () => {
            stopWaiting()
            const webApp = window.Telegram?.WebApp
            if (webApp) {
                resolve(webApp)
            } else {
                reject(new Error('telegram-sdk-empty'))
            }
        }

        script.onerror = () => giveUp('telegram-sdk-error')

        timer = setTimeout(() => giveUp('telegram-sdk-timeout'), timeoutMs)

        document.head.appendChild(script)
    })

    return pending
}
